/**
 *
 * @param {Direction} direc {deg, x, y} - Director vector
 * @returns {Direction} Vector rotated to point the next quadrant
 */
export function perpendicularDirection(direc) {
  if (!direc || !isFinite(direc.deg) || !isFinite(direc.x) || !isFinite(direc.y)) {
    return { deg: 0, x: 1, y: 0 };
  }
  
  return {
    deg: (direc.deg + 90) % 360,
    x: direc.y * -1,
    y: direc.x,
  };
}

/**
 *
 * @param {Position} dot Base dot for line
 * @param {Direction} direc Director vector of line
 * @returns {ExplicitLineParameters} Parameters of explicit equation
 */
export function explicitEquation(dot, direc) {
  if (!dot || !direc || 
      !isFinite(dot.x) || !isFinite(dot.y) ||
      !isFinite(direc.x) || !isFinite(direc.y)) {
    return { m: 0, n: 0 };
  }
  
  const dot2 = { x: dot.x + direc.x, y: dot.y + direc.y };

  const denominator = dot2.x - dot.x;
  
  if (Math.abs(denominator) < 1e-10) {
    return { m: 0, n: dot.y };
  }
  
  const m = (dot2.y - dot.y) / denominator;
  const n = dot.y - m * dot.x;

  if (!isFinite(m) || !isFinite(n)) {
    return { m: 0, n: 0 };
  }

  return { m: m, n: n };
}

/**
 *
 * @param {ExplicitLineParameters} eq1 explicit line 1 equation parameters
 * @param {ExplicitLineParameters} eq2 explicit line 2 equation parameters
 * @returns {Position} position of intersection
 */
export function intersection(eq1, eq2) {
  if (!eq1 || !eq2 || 
      !isFinite(eq1.m) || !isFinite(eq1.n) ||
      !isFinite(eq2.m) || !isFinite(eq2.n)) {
    return { x: 0, y: 0 };
  }
  
  const denominator = eq1.m - eq2.m;
  
  if (Math.abs(denominator) < 1e-10) {
    return { x: 0, y: 0 };
  }
  
  const x = (eq2.n - eq1.n) / denominator;
  const y = eq1.m * x + eq1.n;
  
  if (!isFinite(x) || !isFinite(y)) {
    return { x: 0, y: 0 };
  }
  
  return { x: x, y: y }; 
}

/**
 *
 * @param {Position} originPos Base dot for line
 * @param {Direction} direction Director vector of line
 * @param {Position} baseDot Dot to get projection from
 * @returns {Position} dot projection over (origin + k * direction) line
 */
export function crosswordProjection(originPos, direction, baseDot) {
  if (!originPos || !direction || !baseDot) {
    return { x: 0, y: 0 };
  }
  
  if (!isFinite(originPos.x) || !isFinite(originPos.y) ||
      !isFinite(direction.x) || !isFinite(direction.y) ||
      !isFinite(baseDot.x) || !isFinite(baseDot.y)) {
    return { x: 0, y: 0 };
  }
  
  const l1 = explicitEquation(originPos, direction);
  const l2 = explicitEquation(baseDot, perpendicularDirection(direction));

  const result = intersection(l1, l2);
  
  if (!result || !isFinite(result.x) || !isFinite(result.y)) {
    return { x: 0, y: 0 };
  }
  
  return result;
}

/**
 * Calcula la distancia euclidiana entre dos puntos
 * @param {Position} originPos punto de origen
 * @param {Position} targetPos punto objetivo
 * @returns {number} distancia euclidiana entre los puntos
 */
export function cartesianDistance(originPos, targetPos) {
  if (!originPos || !targetPos || 
      !isFinite(originPos.x) || !isFinite(originPos.y) ||
      !isFinite(targetPos.x) || !isFinite(targetPos.y)) {
    return 0;
  }

  return cartesianDistanceFromDeltas(
    targetPos.x - originPos.x,
    targetPos.y - originPos.y
  );
}

export function cartesianDistanceFromDeltas(xDelta, yDelta) {
  if (!isFinite(xDelta) || !isFinite(yDelta)) {
    return 0;
  }
  
  return Math.hypot(xDelta, yDelta);
}